<script setup lang="ts">
import { computed, watch, toRef } from 'vue';
import { $t } from '@/composables/translations/useTranslate';
import { formatPrice } from '@/utils/formatting/currencyFormatter';
import { formatDayMonthYear } from '@/utils/formatting/dateFormatter';
import { HIDE_REBATES_CHECKOUT } from '@/constants/checkout/config';
import {
  isPaymentScheduleReconciled,
  type PaymentInstallment,
  type PaymentSchedule,
} from '@/utils/checkout/paymentScheduleExtractor';
import type { RebateSummaryLine } from '@/utils/checkout/pricingExtractor';

interface Props {
  totalPrice: number;
  schedule: PaymentSchedule | null;
  priceBeforeRebates?: number;
  rebates?: readonly RebateSummaryLine[];
}

const props = withDefaults(defineProps<Props>(), {
  priceBeforeRebates: undefined,
  rebates: () => [],
});

const schedule = toRef(props, 'schedule');

const isReconciled = computed(() => isPaymentScheduleReconciled(schedule.value, props.totalPrice));

const showInstallments = computed(() => Boolean(schedule.value) && isReconciled.value);

const showRebateRows = computed(
  () => !HIDE_REBATES_CHECKOUT && props.rebates.length > 0 && props.priceBeforeRebates !== undefined
);

watch(
  [schedule, isReconciled],
  ([currentSchedule, reconciled]) => {
    if (!currentSchedule || reconciled) return;
    const sum = currentSchedule.deposit.amount + currentSchedule.balance.amount;
    console.warn(
      `[CheckoutPaymentSchedule] Hiding the payment breakdown: deposit + balance ` +
        `(${sum}) does not match the displayed total (${props.totalPrice}). ` +
        `The BA response carries no insurance/extras — check HIDE_AUXILIARIES_STEP.`
    );
  },
  { immediate: true }
);

function dueLabel(installment: PaymentInstallment): string | null {
  if (!installment.dueDate) return null;
  return $t('ibe.checkout.payment-schedule.due-by', 'By {date} at the latest', {
    date: formatDayMonthYear(installment.dueDate, installment.dueDateOffsetMinutes),
  });
}
</script>

<template>
  <div v-if="showRebateRows || showInstallments" class="checkout-payment-schedule my-8">
    <template v-if="showRebateRows">
      <div class="flex items-center justify-between w-full gap-2 py-2.5">
        <p class="text-body-large text-text-secondary">
          {{ $t('ibe.checkout.summary.price-before-discount', 'Total amount (before discount)') }}
        </p>
        <p class="text-body-large !font-normal text-text-secondary text-right">
          {{ formatPrice(props.priceBeforeRebates ?? 0, true) }}
        </p>
      </div>
      <div
        v-for="rebate in props.rebates"
        :key="rebate.label"
        class="flex items-center justify-between w-full gap-2 pb-2.5"
      >
        <p class="text-body-large text-text-secondary">{{ rebate.label }}</p>
        <p class="text-body-large !font-normal text-text-secondary text-right">
          {{ formatPrice(rebate.amount, true) }}
        </p>
      </div>
    </template>

    <div class="flex items-center justify-between w-full gap-2 pb-5 border-b border-black/60">
      <p class="text-body-large text-text-secondary">
        {{ $t('ibe.checkout.payment-schedule.total', 'Your total price') }}
      </p>
      <p class="text-body-large !font-medium text-text-secondary text-right">
        {{ formatPrice(props.totalPrice, true) }}
      </p>
    </div>

    <template v-if="showInstallments && schedule">
      <div class="pl-5 md:pl-10 py-2.5 border-b border-black/40">
        <div class="flex items-center justify-between w-full gap-2">
          <p class="text-body-large text-text-secondary">
            {{ $t('ibe.checkout.payment-schedule.deposit', 'Deposit') }}
          </p>
          <p class="text-body-large !font-normal text-text-secondary text-right">
            {{ formatPrice(schedule.deposit.amount, true) }}
          </p>
        </div>
        <p v-if="dueLabel(schedule.deposit)" class="text-body text-text-secondary mt-2.5">
          {{ dueLabel(schedule.deposit) }}
        </p>
      </div>

      <div class="pl-5 md:pl-10 py-2.5 border-b border-black/60">
        <div class="flex items-center justify-between w-full gap-2">
          <p class="text-body-large text-text-secondary">
            {{ $t('ibe.checkout.payment-schedule.balance', 'Balance') }}
          </p>
          <p class="text-body-large !font-normal text-text-secondary text-right">
            {{ formatPrice(schedule.balance.amount, true) }}
          </p>
        </div>
        <p v-if="dueLabel(schedule.balance)" class="text-body text-text-secondary mt-2.5">
          {{ dueLabel(schedule.balance) }}
        </p>
      </div>
    </template>
  </div>
</template>
